import { useState, useEffect, useMemo, useRef } from "react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, MapPin, Clock, Phone, Mail, LogOut, MessageSquare, CalendarIcon, Navigation, Settings, MessageCircle, Info, Archive, Camera, MoreVertical, X, User, Star, AlertCircle, Award, RotateCcw } from "lucide-react";
import { ChatInboxDialog } from "./ChatInboxDialog";
import { useToast } from "@/hooks/use-toast";
import { useChatNotifications } from "@/hooks/useChatNotifications";
import { Input } from "@/components/ui/input";
import { Link } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { RespondToJobDialog } from "@/components/RespondToJobDialog";
import { TradesmanTrackingDialog } from "@/components/TradesmanTrackingDialog";
import { TradesmanProfileEdit } from "@/components/TradesmanProfileEdit";
import { JobMessagingDialog } from "@/components/JobMessagingDialog";
import { TradesmanJobDetailDialog } from "@/components/TradesmanJobDetailDialog";
import { LocationControls } from "@/components/LocationControls";
import { JobArchiveDialog } from "@/components/JobArchiveDialog";
import { ArchivedJobsDialog } from "@/components/ArchivedJobsDialog";
import JobRatingSection from "@/components/JobRatingSection";
import {
  JobCardShell,
  JobCardHeaderSection,
  JobCardDivider,
  JobCardBody,
  JobCardDescription,
  JobCardInfoRow,
  JobCardInfoSection,
  JobCardActions,
  JobCardActionRow,
  JobCardButton,
  getUnifiedStatusColor,
  getUnifiedPriorityColor,
} from "@/components/job-card/JobCardComponents";
import RatingDisplay from "@/components/RatingDisplay";
import ContactVerificationDialog from "@/components/ContactVerificationDialog";
import UserRatingsDialog from "@/components/UserRatingsDialog";
import { TradesmanRatingsDialog } from "@/components/TradesmanRatingsDialog";
import { JobStatusManager } from "./JobStatusManager";
import { JobPhotoManager } from "@/components/JobPhotoManager";
import { TradesmanLogo } from "./TradesmanLogo";
import { ModernLogo } from "./ModernLogo";
import { LiveChatWidget } from "./LiveChatWidget";
import { useIsMobile } from "@/hooks/use-mobile";
import { filterJobsByDistance, filterJobsByPreciseAddress, getCachedCoordinates, calculateDistance } from "@/utils/locationUtils";

// Utility function to extract postcode from UK address
const extractPostcode = (address: string): string => {
  if (!address) return 'Location not specified';
  
  // Split by comma and trim each part
  const parts = address.split(',').map(part => part.trim());
  
  // UK postcode pattern: Letters/numbers followed by space and 3 characters
  const postcodeRegex = /^[A-Z]{1,2}[0-9][A-Z0-9]?\s?[0-9][A-Z]{2}$/i;
  
  // Look for postcode in each part
  for (const part of parts) {
    if (postcodeRegex.test(part)) {
      return part;
    }
  }
  
  // Fallback: take last 2 parts after comma split (city, postcode area)
  if (parts.length >= 2) {
    return parts.slice(-2).join(', ');
  }
  
  return address;
};

// Utility function to get the appropriate location display based on job assignment
const getLocationDisplay = (job: Job, currentTradesmanId: string): string => {
  // If job is assigned to current tradesman, show full location
  if (job.tradesman_id === currentTradesmanId) {
    return job.location || 'Location not specified';
  }
  
  // Otherwise, show only postcode for privacy
  return extractPostcode(job.location || '');
};
import { FloatingAppDownload } from "./FloatingAppDownload";
import { ReportJobIssueDialog } from "./ReportJobIssueDialog";
import { LegalDocumentDownloader } from "./LegalDocumentDownloader";
import { PasswordResetDialog } from "./PasswordResetDialog";
import { TradesmanProfileIncompleteDialog } from "./TradesmanProfileIncompleteDialog";
import { JobCardPhoto } from "./JobCardPhoto";
import tradesmanWorkspace from "@/assets/tradesman-workspace.jpg";

// Safe date formatting helper to avoid crashes on invalid dates
const safeFormat = (dateInput: any, fmt: string, fallback = "N/A") => {
  const d = new Date(dateInput);
  return !isNaN(d.getTime()) ? format(d, fmt) : fallback;
};

interface Job {
  id: string;
  title: string;
  description: string;
  location: string;
  // address field removed - should not be visible to tradesmen in general listings
  priority: string;
  status: string;
  budget_min?: number;
  budget_max?: number;
  agreed_cost?: number;
  required_date?: string;
  required_specialties?: string[];
  // job_category removed - now using required_specialties
  response_by: string;
  created_at: string;
  updated_at: string;
  customer_id: string;
  tradesman_id: string | null;
  job_number: number;
  distance?: number; // Distance in miles from tradesman location
}

interface TradesmanDashboardProps {
  onSignOut: () => void;
  chatAction?: string | null;
  chatWithUserId?: string | null;
  focusedJobId?: string | null;
  showPasswordReset?: boolean;
  onPasswordResetChange?: (open: boolean) => void;
}

export const TradesmanDashboard = ({ onSignOut, chatAction, chatWithUserId, focusedJobId, showPasswordReset = false, onPasswordResetChange }: TradesmanDashboardProps) => {
  const [availableJobs, setAvailableJobs] = useState<Job[]>([]);
  const [myJobs, setMyJobs] = useState<Job[]>([]);
  const [pickedJobs, setPickedJobs] = useState<Job[]>([]);
  const [customerRatings, setCustomerRatings] = useState<{[key: string]: {average_rating: number, total_reviews: number}}>({});
  const [pickedJobIds, setPickedJobIds] = useState<Set<string>>(new Set());
  const [assignedJobs, setAssignedJobs] = useState<Job[]>([]);
  const [quotedJobs, setQuotedJobs] = useState<any[]>([]);
  const [userResponses, setUserResponses] = useState<any[]>([]);
  const [currentUserProfile, setCurrentUserProfile] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [trackingJob, setTrackingJob] = useState<Job | null>(null);
  const [messagingJob, setMessagingJob] = useState<Job | null>(null);
  const [showProfileEdit, setShowProfileEdit] = useState(false);
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
  const [selectedJobForArchive, setSelectedJobForArchive] = useState<Job | null>(null);
  const [archivedJobIds, setArchivedJobIds] = useState<Set<string>>(new Set());
  const [mutuallyRatedJobIds, setMutuallyRatedJobIds] = useState<Set<string>>(new Set());
  const [showArchivedJobs, setShowArchivedJobs] = useState(false);
  const [showUserRatingsDialog, setShowUserRatingsDialog] = useState(false);
  const [showTradesmanReviewsDialog, setShowTradesmanReviewsDialog] = useState(false);
  const [photoViewingJob, setPhotoViewingJob] = useState<Job | null>(null);
  const [jobDetailDialog, setJobDetailDialog] = useState<Job | null>(null);
  const [completedPaymentJobIds, setCompletedPaymentJobIds] = useState<string[]>([]);
  const [jobPhotoCounts, setJobPhotoCounts] = useState<{[jobId: string]: number}>({});
  const [dismissedJobIds, setDismissedJobIds] = useState<Set<string>>(new Set());
  const [showChatInbox, setShowChatInbox] = useState(false);
  const [reportJobDialog, setReportJobDialog] = useState<Job | null>(null);
  const [refreshArchivedTrigger, setRefreshArchivedTrigger] = useState(0);
  const [activeTab, setActiveTab] = useState("available");
  const restoredRecentlyRef = useRef<Map<string, number>>(new Map());
  const [forceShowJobIds, setForceShowJobIds] = useState<Set<string>>(new Set());
  const [pinnedCompletedJobIds, setPinnedCompletedJobIds] = useState<Set<string>>(new Set());
  const [pinnedCompletedJobsData, setPinnedCompletedJobsData] = useState<Record<string, Job>>({});
  // Centralized chat notifications (unread + blinking)
  const { unreadCount, shouldBlink, setUnreadCount, setChatOpen } = useChatNotifications(currentUserProfile?.id);
  
  // New states for quoted jobs functionality
  const [amendQuoteDialog, setAmendQuoteDialog] = useState<any>(null);
  const [cancelQuoteDialog, setCancelQuoteDialog] = useState<any>(null);
  const [viewQuoteJobDetails, setViewQuoteJobDetails] = useState<any>(null);
  const isMobile = useIsMobile();
  const { toast } = useToast();

  // Load pinned completed jobs from localStorage when profile is ready
  useEffect(() => {
    if (currentUserProfile?.id) {
      try {
        const raw = localStorage.getItem(`pinnedCompletedJobs:${currentUserProfile.id}`);
        setPinnedCompletedJobIds(raw ? new Set<string>(JSON.parse(raw)) : new Set<string>());
      } catch {}
    }
  }, [currentUserProfile?.id]);

  // Persist pinned completed jobs
  useEffect(() => {
    if (currentUserProfile?.id) {
      try {
        localStorage.setItem(`pinnedCompletedJobs:${currentUserProfile.id}`, JSON.stringify(Array.from(pinnedCompletedJobIds)));
      } catch {}
    }
  }, [pinnedCompletedJobIds, currentUserProfile?.id]);

  // Persist pinned completed job data
  useEffect(() => {
    if (currentUserProfile?.id) {
      try {
        const raw = localStorage.getItem(`pinnedCompletedJobsData:${currentUserProfile.id}`);
        setPinnedCompletedJobsData(raw ? JSON.parse(raw) : {});
      } catch {}
    }
  }, [currentUserProfile?.id]);

  useEffect(() => {
    if (currentUserProfile?.id) {
      try {
        localStorage.setItem(`pinnedCompletedJobsData:${currentUserProfile.id}`, JSON.stringify(pinnedCompletedJobsData));
      } catch {}
    }
  }, [pinnedCompletedJobsData, currentUserProfile?.id]);

  // Completed jobs union (myJobs + pinned) with archive/force/pin overrides
  const completedDisplayedJobs = useMemo(() => {
    const map = new Map<string, Job>();
    myJobs.forEach(j => { if (j.status === 'completed') map.set(j.id, j); });
    Object.values(pinnedCompletedJobsData || {}).forEach((j: any) => {
      if (j && (j as any).id) map.set((j as any).id, { ...(j as any), status: (j as any).status || 'completed' } as Job);
    });
    return Array.from(map.values()).filter(j => !archivedJobIds.has(j.id) || forceShowJobIds.has(j.id) || pinnedCompletedJobIds.has(j.id));
  }, [myJobs, archivedJobIds, forceShowJobIds, pinnedCompletedJobIds, pinnedCompletedJobsData]);

  // Calculate job counts for tab badges
  const jobCounts = useMemo(() => {
    const respondedJobIds = new Set((userResponses || []).map((r: any) => r.job_id));
    return {
      available: availableJobs.length,
      picked: pickedJobs.filter((job: any) => job.status === 'pending' && !respondedJobIds.has(job.id)).length,
      quoted: quotedJobs.length,
      inProgress: myJobs.filter(job => job.status === 'in_progress').length,
      completed: completedDisplayedJobs.length
    };
  }, [availableJobs, pickedJobs, quotedJobs, myJobs, userResponses, completedDisplayedJobs]);

  useEffect(() => {
    fetchJobs();
  }, []);

  // Keep notification state in sync with job-specific messaging dialog
  useEffect(() => {
    setChatOpen(!!messagingJob);
  }, [messagingJob, setChatOpen]);


  // Realtime subscription to profile updates (email/phone verification)
  useEffect(() => {
    let channel: any = null;
    let isCancelled = false;

    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || isCancelled) return;

      channel = supabase
        .channel('tradesman-profile-updates')
        .on('postgres_changes', {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `user_id=eq.${user.id}`,
        }, (payload) => {
          setCurrentUserProfile((prev: any) => ({ ...prev, ...payload.new }));
        })
        .subscribe();
    })();

    return () => {
      isCancelled = true;
      if (channel) supabase.removeChannel(channel);
    };
  }, []);

  // Realtime subscription to job updates for tradesman-related jobs
  useEffect(() => {
    let jobsChannel: any = null;
    let isCancelled = false;

    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || isCancelled) return;

      // Get current user's profile to filter jobs
      const { data: profile } = await supabase
        .from("profiles")
        .select("id")
        .eq("user_id", user.id)
        .maybeSingle();

      if (!profile || isCancelled) return;

      jobsChannel = supabase
        .channel('tradesman-job-updates')
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'jobs',
          filter: `tradesman_id=eq.${profile.id}`,
        }, (payload) => {
          console.log('Job update received for tradesman:', payload);
          // Refresh jobs when status changes for jobs assigned to this tradesman
          fetchJobs();
        })
        // Listen for new jobs being created
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'jobs',
        }, (payload) => {
          console.log('New job created:', payload.new);
          // Refresh available jobs when new jobs are added
          fetchJobs();
        })
        // Also listen for general job updates that might affect available jobs
        .on('postgres_changes', {
          event: 'UPDATE',
          schema: 'public', 
          table: 'jobs',
        }, (payload) => {
          // Only refresh for status changes that might affect job availability
          if (payload.old?.status !== payload.new?.status) {
            console.log('Job status change detected:', payload.old?.status, '->', payload.new?.status);
            fetchJobs();
          }
        })
        .subscribe();
    })();

    return () => {
      isCancelled = true;
      if (jobsChannel) supabase.removeChannel(jobsChannel);
    };
  }, []);

  // Subscribe to archive changes to refresh lists
  useEffect(() => {
    let archiveChannel: any = null;
    let isCancelled = false;

    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || isCancelled) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (!profile || isCancelled) return;

      archiveChannel = supabase
        .channel('tradesman-job-archives')
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'job_user_archives',
          filter: `profile_id=eq.${profile.id}`,
        }, () => {
          console.log('TradesmanDashboard: Archive change detected, refreshing');
          fetchJobs();
        })
        .subscribe();
    })();

    return () => {
      isCancelled = true;
      if (archiveChannel) supabase.removeChannel(archiveChannel);
    };
  }, []);

  // Handle automatic chat inbox opening from email links
  useEffect(() => {
    if (chatAction === 'chat') {
      // Delay slightly to ensure the dashboard is mounted
      const t = setTimeout(() => {
        setShowChatInbox(true);
      }, 500);
      // Clear URL parameters to prevent re-opening on navigation
      window.history.replaceState({}, '', window.location.pathname);
      return () => clearTimeout(t);
    }
  }, [chatAction]);

  const fetchJobs = async () => {
    try {
      // Get the tradesman's profile including location
      const { data: profile } = await supabase
        .from("profiles")
        .select("id, trade_specialties, full_name, user_type, average_rating, total_reviews, email_verified, phone_verified, email, phone, insurance_approved, location, address, logo_url, company_name, service_radius, created_at")
        .eq("user_id", (await supabase.auth.getUser()).data.user?.id)
        .maybeSingle();

      if (!profile) return;
      
      setCurrentUserProfile(profile);

      // Auto-show email verification for new tradesmen
      try {
        if (!profile.email_verified && profile.user_type === 'tradesman' && (profile as any).created_at) {
          const created = new Date((profile as any).created_at);
          const hours = (Date.now() - created.getTime()) / (1000 * 60 * 60);
          if (hours < 24) {
            setTimeout(() => setShowVerificationDialog(true), 1000);
          }
        }
      } catch {}

      // SECURITY: Fetch available jobs via secure RPC function that masks addresses
      // This function returns jobs with only city/general location visible
      // Full addresses are only shown to assigned tradesmen via separate secure functions
      // Prefetch archived job IDs for this tradesman to filter at source
      const { data: archivedRows } = await supabase.rpc('get_archived_jobs_for_current_user');
      let profileArchivedIds = new Set((archivedRows || []).map((a: any) => a.job_id));
      // Ignore recently restored IDs for a short window to prevent flicker
      const nowTs = Date.now();
      for (const [id, ts] of restoredRecentlyRef.current.entries()) {
        if (nowTs - ts > 15000) {
          restoredRecentlyRef.current.delete(id);
        }
      }
      for (const id of restoredRecentlyRef.current.keys()) {
        profileArchivedIds.delete(id);
      }
      setArchivedJobIds(profileArchivedIds);

      console.log('🔍 Calling get_available_jobs_for_tradesmen RPC (address-masked)...');
      const { data: rpcJobs, error: rpcError } = await supabase.rpc('get_available_jobs_for_tradesmen');
      
      if (rpcError) {
        console.error('❌ RPC Error:', rpcError);
        throw rpcError;
      }
      
      console.log(`✅ Fetched ${(rpcJobs || []).length} available jobs (addresses masked for security)`);
      const finalJobs = rpcJobs || [];

      // Get jobs this tradesman has already picked
      const { data: alreadyPickedJobIds } = await supabase
        .from("job_picks")
        .select("job_id")
        .eq("tradesman_id", profile.id);

      const pickedJobIdSet = new Set(alreadyPickedJobIds?.map(p => p.job_id) || []);
      console.log(`🔍 DEBUG: Setting initial pickedJobIds:`, Array.from(pickedJobIdSet));
      setPickedJobIds(pickedJobIdSet);

      // Ensure we have a location field for downstream filters (use general_location fallback)
      // All jobs are now available to all tradesmen until assigned and paid for
      let baseJobs = (finalJobs || [])
        .map((j: any) => {
          return { 
            ...j, 
            status: j.status ?? 'pending', 
            // Preserve full location to allow postcode extraction
            location: j.location ?? j.general_location ?? 'Location not specified',
            address: null // No address in general view
          };
        });
      

      // Filter by specialties overlap only (no more category matching since job_category is removed)
      if (profile.trade_specialties && profile.trade_specialties.length > 0) {
        console.log('🔧 Georgios debug - trade_specialties:', profile.trade_specialties);

        const lowerSpecs = new Set(profile.trade_specialties.map((s: string) => s.toLowerCase()));

        const filteredBySpec = baseJobs.filter((j: any) => {
          const req = (j.required_specialties || []).map((x: string) => String(x).toLowerCase());
          const byOverlap = req.some((x: string) => lowerSpecs.has(x));
          
          console.log(`🔧 Job ${j.title} (${j.id}):`, {
            required_specialties: j.required_specialties,
            byOverlap,
            finalMatch: byOverlap
          });
          
          return byOverlap;
        });

        if (filteredBySpec.length > 0) {
          baseJobs = filteredBySpec;
        }
        console.log('🔧 Georgios debug - jobs after specialty filter:', filteredBySpec.length);
      }
      // Filter jobs by location distance using business address (user's service radius)
      let locationFilteredJobs: Job[] = [];
      const userRadius = profile.service_radius || 25; // Default to 25 miles if not set

      const originLocation = (profile.address && profile.address.trim().length > 0) ? profile.address : profile.location;
      console.log('🏢 Tradesman profile address:', profile.address);
      console.log('🏙️ Tradesman profile location:', profile.location);
      console.log('📊 Jobs to filter:', baseJobs.length);
      console.log('🎯 User service radius:', userRadius, 'miles');

      if (originLocation) {
        // Use precise business address for distance calculations - require exact coordinates
        try {
          console.log('🏢 Starting PRECISE address filtering with business location:', originLocation);
          const preciseFiltered = await filterJobsByPreciseAddress(baseJobs || [], originLocation, userRadius);
          console.log('📏 Jobs after PRECISE address filtering:', preciseFiltered.length);
          
          // Log each job with distance
          preciseFiltered.forEach((job: any) => {
            console.log(`Job ${job.title}: distance = ${job.distance} miles`);
          });
          
          locationFilteredJobs = preciseFiltered;
          console.log(`✅ Precise business address filtering complete: ${baseJobs.length} → ${locationFilteredJobs.length} jobs within ${userRadius} miles of ${originLocation}`);
          console.log('DBG after precise distance filter, contains target?', locationFilteredJobs.some((j: any) => j.id === '0bd20f57-1253-4f72-9cc2-1b7f0a7e66c7'));
        } catch (error) {
          console.error('💥 Error filtering jobs by business address:', error);
          locationFilteredJobs = [];
        }
      } else {
        console.warn('⚠️ No business address available - cannot filter jobs by location');
        locationFilteredJobs = [];
      }

      // Filter out archived jobs from available opportunities using prefetched archive set
      locationFilteredJobs = locationFilteredJobs.filter(job => !profileArchivedIds.has(job.id));
      
      // Filter out picked jobs from available jobs
      locationFilteredJobs = locationFilteredJobs.filter(job => !pickedJobIdSet.has(job.id));
      console.log('DBG after archive and picked filter, contains target?', locationFilteredJobs.some((j: any) => j.id === '0bd20f57-1253-4f72-9cc2-1b7f0a7e66c7'));
      
      // Save available jobs
      setAvailableJobs(locationFilteredJobs);

      // Get picked jobs using the dedicated RPC function
      const { data: pickedJobsData, error: pickedJobsError } = await supabase.rpc('get_picked_jobs_for_tradesman');
      if (pickedJobsError) {
        console.error('Error fetching picked jobs:', pickedJobsError);
      }
      const pickedJobs: Job[] = (pickedJobsData || []).map((j: any) => ({
        ...j,
        status: j.status ?? 'pending',
        location: j.location ?? 'Location not specified',
        address: null,
        distance: null,
        isPicked: true,
        isAssigned: false,
      }));
      
      console.log("Final picked jobs (RPC):", pickedJobs.map(j => ({ id: j.id, title: j.title, status: j.status })));
      
      console.log("Final picked jobs:", pickedJobs.map(j => ({ id: j.id, title: j.title, status: j.status })));

      // Fetch customer ratings for picked jobs using the new RPC function
      if (pickedJobs.length > 0) {
        const ratingsPromises = pickedJobs.map(async (job) => {
          const { data: customerRating, error } = await supabase
            .rpc('get_customer_rating_for_job_if_picked', { job_id: job.id });
          
          if (!error && customerRating && customerRating.length > 0) {
            return {
              jobId: job.id,
              rating: customerRating[0]
            };
          }
          return null;
        });

        const ratingsResults = await Promise.all(ratingsPromises);
        const ratingsMap = ratingsResults
          .filter(result => result !== null)
          .reduce((acc: any, result: any) => {
            acc[result.jobId] = result.rating;
            return acc;
          }, {});
        
        console.log("Customer ratings fetched:", ratingsMap);
        setCustomerRatings(ratingsMap);
      }

        const { data: assignedJobsData, error: assignedJobsError } = await supabase
        .from("jobs")
        .select("*")
        .eq("tradesman_id", profile.id)
        .in("status", ["assigned", "in_progress", "completed", "quote_accepted"])
        .order("created_at", { ascending: false });

      // Fetch this tradesman's responses with a quote (no join to avoid jobs RLS)
      const { data: responsesForTrader, error: responsesForTraderError } = await supabase
        .from("job_responses")
        .select("*")
        .eq("tradesman_id", profile.id)
        .not("estimated_cost", "is", null);

      console.log("🔍 Debug - Profile ID:", profile.id);
      console.log("🔍 Debug - Responses for trader:", responsesForTrader);
      console.log("🔍 Debug - Responses error:", responsesForTraderError);

      if (responsesForTraderError) throw responsesForTraderError;

      // For each response, fetch job details via secure RPC that authorizes responders
      const quotedJobsData = (
        await Promise.all(
          (responsesForTrader || []).map(async (resp: any) => {
            const { data: jobDetails } = await supabase.rpc('get_job_details_for_responder', { job_id: resp.job_id });
            const job = jobDetails?.[0];
            // Exclude jobs that moved forward or are assigned to someone else
            const excludedStatuses = ['in_progress', 'completed', 'cancelled', 'canceled', 'assigned'];
            const assignedToAnother = (job as any)?.tradesman_id && (job as any).tradesman_id !== profile.id;
            // Show any responded job except ones already in progress/completed/cancelled/assigned or assigned to another tradesman
            if (job && !excludedStatuses.includes(job.status) && !assignedToAnother) {
              // Structure data to match what the rendering code expects
              return { 
                ...resp,  // Include response details at top level
                jobs: job // Include job details under 'jobs' property
              };
            }
            return null;
          })
        )
      ).filter(Boolean) as any[];

      console.log("🔍 Debug - Final quotedJobsData:", quotedJobsData);
      console.log("🔍 Debug - quotedJobsData length:", quotedJobsData.length);

      if (assignedJobsError) {
        console.error("Error fetching assigned jobs:", assignedJobsError);
        throw assignedJobsError;
      }

      // Combine picked, quoted, and assigned jobs, ensuring no duplicates
      const assignedJobs = assignedJobsData || [];
      const quotedJobs = quotedJobsData || [];
      const assignedJobIds = new Set(assignedJobs.map((job: any) => job.id));
      const allJobIds = new Set([
        ...assignedJobs.map((job: any) => job.id),
        ...quotedJobs.map((job: any) => job.id),
        ...pickedJobs.map((job: any) => job.id)
      ]);
      // Remove duplicates - prioritize assigned/quoted jobs over picked jobs to avoid duplicates
      const uniquePickedJobs = pickedJobs.filter((job: any) => !allJobIds.has(job.id) || (!assignedJobIds.has(job.id) && !quotedJobs.some((q: any) => q.id === job.id)));
      let myJobsData = [...uniquePickedJobs, ...quotedJobs, ...assignedJobs];
      
      // Apply precise distance calculations to all job types using business address
      if (originLocation) {
        try {
          console.log('📏 Applying PRECISE distance calculations to myJobs data...');
          myJobsData = await filterJobsByPreciseAddress(myJobsData, originLocation, 999); // High limit to include all with precise distances
          console.log('📏 MyJobs precise distances calculated');
        } catch (error) {
          console.error('💥 Error calculating precise distances for myJobs:', error);
        }
      }
      
      // Apply distance calculations to quoted jobs data as well
      if (originLocation && quotedJobsData.length > 0) {
        try {
          console.log('📏 Applying distance calculations to quoted jobs data...');
          const originCoords = await getCachedCoordinates(originLocation);
          
          const quotedJobsWithDistance = await Promise.all(
            quotedJobsData.map(async (response) => {
              if (response.jobs?.location && originCoords) {
                const jobLocation = response.jobs.location;
                const jobCoords = await getCachedCoordinates(jobLocation);
                
                if (jobCoords) {
                  const distance = calculateDistance(
                    originCoords.latitude, 
                    originCoords.longitude, 
                    jobCoords.latitude, 
                    jobCoords.longitude
                  );
                  response.jobs.distance = Math.round(distance * 10) / 10;
                }
              }
              return response;
            })
          );
          
          console.log('📏 Quoted jobs distances calculated');
          setQuotedJobs(quotedJobsWithDistance);
        } catch (error) {
          console.error('💥 Error calculating distances for quoted jobs:', error);
          setQuotedJobs(quotedJobsData);
        }
      } else {
        setQuotedJobs(quotedJobsData);
      }
      
      console.log("Quoted jobs found:", quotedJobsData.map(j => ({ id: j.id, title: j.jobs?.title, status: j.jobs?.status })));
      console.log("🔍 Debug - Setting quotedJobs with data:", quotedJobsData);
      
      console.log("Final myJobsData:", myJobsData.map(j => ({ 
        id: j.id, 
        title: j.title, 
        status: j.status,
        distance: j.distance,
        isPicked: pickedJobs.some(u => u.id === j.id),
        isAssigned: assignedJobIds.has(j.id)
      })));

      // Filter out archived jobs from myJobsData using prefetched archives
      myJobsData = myJobsData.filter((job: any) => !profileArchivedIds.has(job.id));
      console.log(`Filtered myJobs: total → ${myJobsData.length} non-archived`);

      // Force-hide completed jobs that have mutual reviews (in case archives lag)
      try {
        const completedWithAssignee = myJobsData.filter((j: any) => j.status === 'completed' && j.tradesman_id && j.customer_id);
        const results = await Promise.all(
          completedWithAssignee.map(async (j: any) => {
            const { data: r1 } = await supabase
              .from('ratings')
              .select('id')
              .eq('job_id', j.id)
              .eq('reviewer_id', j.customer_id)
              .eq('reviewee_id', j.tradesman_id)
              .maybeSingle();

            const { data: r2 } = await supabase
              .from('ratings')
              .select('id')
              .eq('job_id', j.id)
              .eq('reviewer_id', j.tradesman_id)
              .eq('reviewee_id', j.customer_id)
              .maybeSingle();

            return r1 && r2 ? j.id : null;
          })
        );
        setMutuallyRatedJobIds(new Set(results.filter(Boolean) as string[]));
      } catch (e) {
        console.warn('Mutual rating check failed (tradesman), proceeding without force-hide', e);
      }

      // Separately fetch payment information
      let completedPaymentJobIds: string[] = [];
      try {
        // Use secure function to get tradesman payments
        const { data: paymentData } = await supabase.rpc('get_tradesman_payments');
        const jobIds = (myJobsData || []).map((job: any) => job.id);
        completedPaymentJobIds = paymentData?.filter(p => p.status === "completed" && jobIds.includes(p.job_id)).map(p => p.job_id) || [];
        setCompletedPaymentJobIds(completedPaymentJobIds);
      } catch (paymentError) {
        console.error("Error fetching payment data:", paymentError);
        // Continue without payment filtering if this fails
      }

      // Fetch job responses for this tradesman
      const { data: responsesData, error: responsesError } = await supabase
        .from("job_responses")
        .select(`
          *,
          jobs!inner(*)
        `)
        .eq("tradesman_id", profile.id)
        .order("created_at", { ascending: false });

      if (responsesError) throw responsesError;

      // Separate quoted jobs from regular responses
      let responseQuotedJobsData = responsesData || [];
      let filteredResponses = responsesData || [];
      
      if (responsesData && responsesData.length > 0) {
        try {
          // Fetch archive data for response jobs via secure RPC (avoids RLS issues)
          const { data: archivedForUser } = await supabase
            .rpc('get_archived_jobs_for_current_user');
          const archivedResponseJobIds = (archivedForUser || [])
            .map((a: any) => a.job_id)
            .filter((id: string) => !restoredRecentlyRef.current.has(id));
          
          console.log("Filtering quoted jobs from responses:", responsesData.length);
          // Filter quoted jobs: jobs with responses that have status 'quoted' OR haven't moved to in-progress due to payment
          responseQuotedJobsData = responsesData.filter((response: any) => {
            const job = response.jobs;
            const isArchived = archivedResponseJobIds.includes(job.id);
            const hasCompletedPayment = completedPaymentJobIds.includes(job.id);
            const isInProgressOrCompleted = ['in_progress', 'completed'].includes(job.status);
            const isAssignedToAnotherTradesman = job.tradesman_id && job.tradesman_id !== profile.id;
            
            const shouldInclude = !isArchived && 
                   job.status !== 'cancelled' && 
                   job.status !== 'canceled' &&
                   !isAssignedToAnotherTradesman && // Exclude jobs assigned to other tradesmen
                   (job.status === 'quoted' || !isInProgressOrCompleted);
            
            console.log(`Job ${job.title} (${job.id.slice(0, 8)}) - Status: ${job.status}, Included: ${shouldInclude}`, {
              isArchived,
              hasCompletedPayment, 
              isInProgressOrCompleted,
              isAssignedToAnotherTradesman,
              jobTradesmanId: job.tradesman_id,
              currentProfileId: profile.id,
              jobStatus: job.status
            });
            
            // Keep in quoted tab if: has response, not archived, not cancelled, not assigned to another tradesman, and (status is 'quoted' OR not in progress/completed)
            return shouldInclude;
          });
          
          console.log("Final quoted jobs count:", responseQuotedJobsData.length);
          
          // Filter regular responses (for backward compatibility - these are the old "quoted" jobs)
          filteredResponses = responsesData.filter((response: any) => {
            const job = response.jobs;
            const isArchived = archivedResponseJobIds.includes(job.id);
            
            // Exclude if job is in_progress
            if (job.status === 'in_progress') {
              return false;
            }
            
            // Exclude if job is cancelled
            if (job.status === 'cancelled' || job.status === 'canceled') {
              return false;
            }
            
            // Exclude if job is archived
            if (isArchived) {
              return false;
            }
            
            return true;
          });
        } catch (filterError) {
          console.error("Error filtering responses:", filterError);
          // Continue with unfiltered responses if filtering fails
        }
      }
      
      // Filter out customer-archived jobs unless they're ongoing
      const filteredMyJobs = myJobsData.filter((job: any) => {
        const isArchived = archivedJobIds.has(job.id);
        
        // Always show assigned, in-progress, or quote_accepted jobs even if archived
        if (job.status === 'assigned' || job.status === 'in_progress' || job.status === 'quote_accepted') {
          return true;
        }
        
        // Hide archived jobs in all other cases (including completed)
        if (isArchived) {
          return false;
        }
        
        return true;
      });
      
      // Exclude jobs already quoted by this tradesman from Picked tab
      const respondedJobIds = new Set((responsesForTrader || []).map((r: any) => r.job_id));
      const pickedJobsFiltered = pickedJobs.filter((job: any) => job.status === 'pending' && !respondedJobIds.has(job.id));
      const assignedJobsFiltered = filteredMyJobs.filter((job: any) => assignedJobIds.has(job.id));
      
      setMyJobs(filteredMyJobs as Job[]);
      setPickedJobs(pickedJobsFiltered as Job[]);
      setAssignedJobs(assignedJobsFiltered as Job[]);
      // setQuotedJobs(responseQuotedJobsData); // Avoid overwriting quotedJobs computed via secure RPC
      setUserResponses(filteredResponses);

      // Fetch photo counts for all jobs (available + picked/assigned) for verified tradesmen
      if (profile.insurance_approved) {
        try {
          const allJobIds = [
            ...locationFilteredJobs.map(job => job.id),
            ...filteredMyJobs.map(job => job.id),
            ...quotedJobs.map(job => job.jobs?.id).filter(Boolean) // Include quoted jobs
          ];
          
          if (allJobIds.length > 0) {
            const { data: photoCounts } = await supabase
              .from('job_photos')
              .select('job_id')
              .in('job_id', allJobIds);
            
            // Count photos per job
            const counts: {[jobId: string]: number} = {};
            photoCounts?.forEach(photo => {
              counts[photo.job_id] = (counts[photo.job_id] || 0) + 1;
            });
            
            setJobPhotoCounts(counts);
            console.log('📷 Photo counts fetched for all jobs:', counts);
          }
        } catch (error) {
          console.error('Error fetching photo counts:', error);
        }
      }
    } catch (error: any) {
      toast({
        title: "Error fetching jobs",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    onSignOut();
  };

  const handleRemovePickedJob = async (job: Job, sendNotInterested = false) => {
    if (!currentUserProfile) return;

    try {
      // 1) Optionally notify the customer BEFORE removing the pick (RLS needs the pick to exist)
      if (sendNotInterested) {
        try {
          const { data: fullJob, error: jobError } = await supabase
            .from('jobs')
            .select('customer_id')
            .eq('id', job.id)
            .single();

          if (jobError) {
            console.error('Error fetching job customer:', jobError);
          } else if (fullJob?.customer_id) {
            // Use the send-message edge function to trigger email notifications
            const { error: messageError } = await supabase.functions.invoke('send-message', {
              body: {
                job_id: job.id,
                recipient_id: fullJob.customer_id,
                content: `Thank you for considering ${currentUserProfile.company_name || 'us'} for your job "${job.title}". After reviewing the requirements, we won't be able to take on this project at this time. We appreciate your understanding and wish you the best of luck finding the right tradesman for the job.`,
                message_type: 'text'
              }
            });

            if (messageError) {
              console.error('Error sending not interested message:', messageError);
              // Continue flow even if message fails
            }
          }
        } catch (msgError) {
          console.error('Failed to send not interested message:', msgError);
        }
      }

      // 2) Archive the job for this tradesman BEFORE removing the pick (RLS may rely on pick)
      try {
        const reasonText = sendNotInterested
          ? 'Not interested - notified customer and removed pick'
          : 'Not interested - removed pick quietly';
        const { error: archiveError } = await supabase.rpc('archive_job_for_current_user', {
          p_job_id: job.id,
          p_reason: reasonText,
        });
        if (archiveError) {
          console.error('Archive RPC failed:', archiveError);
        }

        if (archiveError) {
          console.error('Error archiving job:', archiveError);
        } else {
          // Optimistic UI: hide immediately
          setArchivedJobIds((prev) => new Set([...prev, job.id]));
        }
      } catch (archiveEx) {
        console.error('Failed to archive job:', archiveEx);
      }

      // 3) Finally, remove from job_picks
      const { error: removeError } = await supabase
        .from('job_picks')
        .delete()
        .eq('job_id', job.id)
        .eq('tradesman_id', currentUserProfile.id);

      if (removeError) throw removeError;

      // Update picked jobs state immediately for instant UI feedback
      setPickedJobIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(job.id);
        return newSet;
      });

      toast({
        title: 'Job removed from picked list',
        description: sendNotInterested
          ? 'Not interested message sent and job archived'
          : 'Job archived and removed from your picked list',
      });

      fetchJobs();
    } catch (error) {
      console.error('Error removing picked job:', error);
      toast({
        title: 'Error',
        description: 'Failed to update job pick status',
        variant: 'destructive',
      });
    }
  };

  const handleArchiveJob = (job: Job) => {
    setSelectedJobForArchive(job);
    setIsArchiveDialogOpen(true);
  };

  // New handlers for quoted jobs functionality
  const handleAmendQuote = (response: any) => {
    setAmendQuoteDialog(response);
  };

  const handleCancelQuote = (response: any) => {
    setCancelQuoteDialog(response);
  };

  const handleViewQuoteJobDetails = (response: any) => {
    setViewQuoteJobDetails(response);
  };

  const handleArchiveSuccess = () => {
    console.log("Archive success callback triggered");
    if (selectedJobForArchive) {
      setArchivedJobIds(prev => new Set([...prev, selectedJobForArchive.id]));
      setPinnedCompletedJobIds(prev => {
        const next = new Set(prev);
        next.delete(selectedJobForArchive.id);
        return next;
      });
      setPinnedCompletedJobsData(prev => {
        const next = { ...prev } as Record<string, Job>;
        delete (next as any)[selectedJobForArchive.id];
        return next;
      });
    }
    fetchJobs();
    // Trigger refresh of archived jobs
    setRefreshArchivedTrigger(prev => prev + 1);
  };

  const handlePickJobForReview = async (job: Job) => {
    if (!currentUserProfile) return;

    try {
      // Update picked jobs state immediately for instant UI feedback FIRST
      setPickedJobIds(prev => new Set([...prev, job.id]));

      // Remove the job from available jobs for instant UI feedback
      setAvailableJobs(prev => prev.filter(availableJob => availableJob.id !== job.id));

      // Add the job to picked jobs state for instant UI feedback
      setPickedJobs(prev => {
        const exists = prev.some(pickedJob => pickedJob.id === job.id);
        if (!exists) {
          return [...prev, job];
        }
        return prev;
      });

      // Insert into job_picks table
      const { error } = await supabase
        .from('job_picks')
        .upsert(
          { job_id: job.id, tradesman_id: currentUserProfile.id },
          { onConflict: 'job_id,tradesman_id', ignoreDuplicates: true }
        );

      if (error && (error as any).code !== '23505') throw error;

      toast({
        title: "Job picked for review!",
        description: "You can find it under the Picked tab.",
      });
      
      // Refresh the jobs list to reflect the change, but do it after a small delay
      // to ensure the UI state updates aren't overridden
      setTimeout(() => {
        fetchJobs();
      }, 100);
    } catch (error: any) {
      console.error('Error picking job:', error);
      
      // Revert state changes on error
      setPickedJobIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(job.id);
        return newSet;
      });
      setAvailableJobs(prev => {
        const exists = prev.some(availableJob => availableJob.id === job.id);
        if (!exists) {
          return [...prev, job];
        }
        return prev;
      });
      setPickedJobs(prev => prev.filter(pickedJob => pickedJob.id !== job.id));
      
      toast({
        title: "Error",
        description: error.message || "Failed to pick job. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleUnpickJob = async (job: Job) => {
    if (!currentUserProfile) return;

    try {
      // Update UI state immediately FIRST for instant feedback
      setPickedJobIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(job.id);
        console.log(`🔍 DEBUG: Removing job ${job.title} from pickedJobIds`);
        return newSet;
      });

      // Remove the job from picked jobs state
      setPickedJobs(prev => prev.filter(pickedJob => pickedJob.id !== job.id));

      // Add the job back to available jobs for instant UI feedback
      setAvailableJobs(prev => {
        const exists = prev.some(availableJob => availableJob.id === job.id);
        if (!exists && job.status === 'pending') {
          console.log(`🔍 DEBUG: Adding job ${job.title} back to availableJobs`);
          return [...prev, job];
        }
        return prev;
      });

      // Then remove from job_picks table
      const { error } = await supabase
        .from('job_picks')
        .delete()
        .eq('job_id', job.id)
        .eq('tradesman_id', currentUserProfile.id);

      if (error) throw error;

      toast({
        title: "Job unpicked successfully!",
        description: "Job moved back to available jobs.",
      });
      
      // Refresh the jobs list after a small delay to avoid overriding UI state
      setTimeout(() => {
        fetchJobs();
      }, 100);
    } catch (error: any) {
      console.error('Error unpicking job:', error);
      
      // Revert state changes on error
      setPickedJobIds(prev => new Set([...prev, job.id]));
      setPickedJobs(prev => {
        const exists = prev.some(pickedJob => pickedJob.id === job.id);
        if (!exists) {
          return [...prev, job];
        }
        return prev;
      });
      setAvailableJobs(prev => prev.filter(availableJob => availableJob.id !== job.id));
      
      toast({
        title: "Error",
        description: error.message || "Failed to unpick job. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDismissJob = async (job: Job) => {
    if (!currentUserProfile) return;

    try {
      const { error } = await supabase
        .from('job_dismissals')
        .insert({
          job_id: job.id,
          tradesman_id: currentUserProfile.id,
          reason: 'Not interested'
        });

      if (error) throw error;

      // Add to dismissed jobs set to immediately hide from UI
      setDismissedJobIds(prev => new Set([...prev, job.id]));
      
      toast({
        title: "Job dismissed",
        description: "This job has been removed from your available jobs list.",
      });

      // Refresh the jobs list to get updated data
      fetchJobs();
    } catch (error: any) {
      console.error('Error dismissing job:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to dismiss job. Please try again.",
        variant: "destructive",
      });
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "emergency": return "bg-red-100 text-red-800";
      case "urgent": return "bg-orange-100 text-orange-800";
      case "standard": return "bg-blue-100 text-blue-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending": return "bg-yellow-100 text-yellow-800";
      case "picked": return "bg-blue-100 text-blue-800";
      case "assigned": return "bg-green-100 text-green-800";
      case "in_progress": return "bg-orange-100 text-orange-800";
      case "completed": return "bg-green-100 text-green-800";
      case "cancelled": return "bg-red-100 text-red-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const formatBudget = (min?: number, max?: number) => {
    if (!min && !max) return "Budget not specified";
    if (min && max) return `£${min} - £${max}`;
    if (min) return `From £${min}`;
    if (max) return `Up to £${max}`;
    return "";
  };

  const filteredJobs = availableJobs
    .filter(job => !archivedJobIds.has(job.id))
    .filter(job => !dismissedJobIds.has(job.id))
    .filter(job => {
      const isPicked = pickedJobIds.has(job.id);
      if (isPicked) {
        console.log(`🔍 DEBUG: Job ${job.title} (${job.id}) is picked, hiding from available`);
      }
      return !isPicked; // Hide jobs that are already picked
    })
    // Allow multiple tradesmen to pick/quote the same job - only hide after assignment and payment
    .filter(job =>
      job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (job.location || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

  const isResponseTimeUrgent = (responseBy: string) => {
    const timeLeft = new Date(responseBy).getTime() - new Date().getTime();
    return timeLeft < 30 * 60 * 1000; // 30 minutes
  };

  return (
    <div className="min-h-screen bg-gradient-subtle">
      {/* Compact Header */}
      <header className="relative bg-gradient-to-r from-background via-background/95 to-background backdrop-blur-lg border-b border-border/50 shadow-sm sticky top-0 z-50">
        <div className="absolute inset-0 bg-gradient-hero opacity-5"></div>
        <div className="relative max-w-7xl mx-auto px-3 sm:px-4">
          <div className={`flex ${isMobile ? 'flex-col gap-2' : 'justify-between items-center'} ${isMobile ? 'py-2' : 'py-3'}`}>
            {/* Top row - Logo and basic info */}
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <div className="relative">
                  <ModernLogo size="sm" showTagline={false} />
                  {!isMobile && <p className="text-xs text-muted-foreground/60 mt-1">Tradesman Dashboard</p>}
                </div>
                 {currentUserProfile && !isMobile && (
                  <div className="flex items-center gap-3 ml-2">
                    <div 
                      className="bg-accent/10 px-3 py-1 rounded-full border border-accent/20 cursor-pointer hover:bg-accent/20 transition-all duration-200 hover:scale-105"
                      onClick={() => setShowTradesmanReviewsDialog(true)}
                    >
                      <RatingDisplay 
                        rating={currentUserProfile.average_rating} 
                        totalReviews={currentUserProfile.total_reviews} 
                        size="sm"
                        onClick={() => setShowTradesmanReviewsDialog(true)}
                      />
                    </div>
                    
                    {/* Verification Status */}
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        <Badge 
                          variant={currentUserProfile.email_verified ? "default" : "destructive"} 
                          className="text-xs px-2 py-0.5"
                        >
                          {currentUserProfile.email_verified ? "✓" : "✗"}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-1">
                        <Phone className="h-3 w-3" />
                        <Badge 
                          variant={currentUserProfile.phone_verified ? "default" : "destructive"} 
                          className="text-xs px-2 py-0.5"
                        >
                          {currentUserProfile.phone_verified ? "✓" : "✗"}
                        </Badge>
                      </div>
                      {(!currentUserProfile?.email_verified || !currentUserProfile?.phone_verified) && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => setShowVerificationDialog(true)} 
                          className="text-xs h-7 px-2 border-accent-orange/30"
                        >
                          Verify
                        </Button>
                      )}
                    </div>

                      {/* Desktop Action Buttons */}
                      <div className="flex items-center gap-2">
                        {/* Unread Messages Count - Emphasized Red Blinking Button Above Messages Button */}
                        <div className="relative flex flex-col items-center">
                          {shouldBlink && !showChatInbox && !messagingJob && unreadCount > 0 && (
                            <div className="mb-1">
                              <Button
                                size="sm"
                                className="bg-red-500 hover:bg-red-600 text-white rounded-full h-8 w-8 p-0 animate-pulse shadow-lg border-2 border-red-600"
                                onClick={() => { setChatOpen(true); setShowChatInbox(true); }}
                              >
                                <span className="text-xs font-bold">{unreadCount}</span>
                              </Button>
                            </div>
                          )}
                          {/* Chat Inbox */}
                          <Button 
                            variant="outline" 
                            size="default" 
                            className="relative"
                            aria-label="Message Inbox"
                            onClick={() => { setChatOpen(true); setShowChatInbox(true); }}
                          >
                            <MessageCircle className="h-4 w-4 mr-2" />
                            Chat
                          </Button>
                        </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setShowArchivedJobs(true)}
                      >
                        <Archive className="h-4 w-4 mr-2" />
                        Archived
                      </Button>
                      <Button asChild variant="outline" size="sm" className="h-8 w-8 p-0" aria-label="About">
                        <Link to="/about">
                          <Info className="h-4 w-4" />
                        </Link>
                      </Button>
                      <Button
                        onClick={() => setShowProfileEdit(true)} 
                        variant="outline" 
                        size="sm"
                      >
                        <Settings className="h-4 w-4 mr-2" />
                        Profile
                      </Button>
                      <Button onClick={handleSignOut} variant="outline" size="sm">
                        <LogOut className="h-4 w-4 mr-2" />
                        Sign Out
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Mobile: Compact layout */}
              {isMobile && (
                <div className="flex items-center gap-2">
                  {currentUserProfile && (
                    <div 
                      className="bg-accent/10 px-2 py-1 rounded-full border border-accent/20 cursor-pointer hover:bg-accent/20 active:scale-95 transition-all duration-200"
                      onClick={() => setShowTradesmanReviewsDialog(true)}
                    >
                      <RatingDisplay 
                        rating={currentUserProfile.average_rating} 
                        totalReviews={currentUserProfile.total_reviews} 
                        size="sm"
                        onClick={() => setShowTradesmanReviewsDialog(true)}
                      />
                    </div>
                  )}
                  <Button onClick={handleSignOut} variant="outline" size="sm" className="h-8 w-8 p-0">
                    <LogOut className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>

            {/* Mobile: Compact verification and actions row */}
            {isMobile && currentUserProfile && (
              <div className="flex items-center justify-between w-full gap-2">
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    <Mail className="h-3 w-3 text-muted-foreground" />
                    <Badge 
                      variant={currentUserProfile.email_verified ? "default" : "destructive"} 
                      className="text-xs px-1 py-0.5 h-4"
                    >
                      {currentUserProfile.email_verified ? "✓" : "✗"}
                    </Badge>
                    <Phone className="h-3 w-3 text-muted-foreground ml-1" />
                    <Badge 
                      variant={currentUserProfile.phone_verified ? "default" : "destructive"} 
                      className="text-xs px-1 py-0.5 h-4"
                    >
                      {currentUserProfile.phone_verified ? "✓" : "✗"}
                    </Badge>
                  </div>
                  {(!currentUserProfile?.email_verified || !currentUserProfile?.phone_verified) && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setShowVerificationDialog(true)} 
                      className="h-6 px-2 text-xs border-accent-orange/30 text-accent-orange"
                    >
                      Verify
                    </Button>
                  )}
                </div>
                
                <div className="flex gap-2">
                  {/* Chat Inbox - Mobile */}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-7 w-7 p-0 relative"
                    aria-label="Message Inbox"
                    onClick={() => setShowChatInbox(true)}
                  >
                    <MessageCircle className="h-3 w-3" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-7 w-7 p-0"
                    aria-label="Archived Jobs"
                    onClick={() => setShowArchivedJobs(true)}
                  >
                    <Archive className="h-3 w-3" />
                  </Button>
                  <Button asChild variant="outline" size="sm" className="h-7 w-7 p-0" aria-label="About">
                    <Link to="/about">
                      <Info className="h-3 w-3" />
                    </Link>
                  </Button>
                  <Button
                    onClick={() => setShowProfileEdit(true)} 
                    variant="outline" 
                    size="sm" 
                    className="h-7 px-2"
                  >
                    <Settings className="h-3 w-3 mr-1" />
                    <span className="text-xs">Profile</span>
                  </Button>
                </div>
              </div>
            )}

            {/* Insurance warning - compact */}
            {currentUserProfile?.user_type === 'tradesman' && !currentUserProfile?.insurance_approved && (
              <div className={`bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2 text-xs text-yellow-800 ${isMobile ? 'w-full text-center' : ''}`}>
                <span className="font-medium">Insurance approval required to view the available jobs in your area</span>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-hero">
        <div className="absolute inset-0">
          <img 
            src={tradesmanWorkspace} 
            alt="Professional tradesman workspace" 
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-primary/60 mix-blend-multiply" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <TradesmanLogo 
                logoUrl={currentUserProfile?.logo_url}
                companyName={currentUserProfile?.company_name}
                size="xl"
                className="mr-4"
              />
              <div>
                <h1 className={`${isMobile ? 'text-2xl' : 'text-3xl sm:text-4xl lg:text-5xl'} font-bold text-white mb-0`}>
                  {currentUserProfile?.company_name || 'Welcome Back'}
                </h1>
                <p className={`${isMobile ? 'text-sm' : 'text-lg'} text-white/80`}>
                  Welcome back, {currentUserProfile?.full_name?.split(' ')[0] || 'Professional'}
                </p>
              </div>
            </div>
            <p className={`${isMobile ? 'text-base' : 'text-xl'} text-white/90 mb-6 sm:mb-8 max-w-2xl mx-auto`}>
              Find quality jobs that match your expertise and grow your business with verified customers
            </p>
            {currentUserProfile && (
              <div className={`flex ${isMobile ? 'flex-col gap-3' : 'flex-col sm:flex-row gap-4'} justify-center items-center`}>
                <div className={`bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg ${isMobile ? 'px-4 py-2' : 'px-6 py-3'} text-white ${isMobile ? 'w-full max-w-xs' : ''}`}>
                  <div className={`${isMobile ? 'text-xs' : 'text-sm'} opacity-75`}>Your Rating</div>
                  <div className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold`}>
                    {currentUserProfile.average_rating ? `${currentUserProfile.average_rating.toFixed(1)}★` : 'New'}
                  </div>
                </div>
                <div className={`bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg ${isMobile ? 'px-4 py-2' : 'px-6 py-3'} text-white ${isMobile ? 'w-full max-w-xs' : ''}`}>
                  <div className={`${isMobile ? 'text-xs' : 'text-sm'} opacity-75`}>Total Reviews</div>
                  <div className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold`}>{currentUserProfile.total_reviews || 0}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content with Tabs */}
      <main className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${isMobile ? 'py-6' : 'py-8 sm:py-12'}`}>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="relative mb-8">
            {/* Gradient overlays to indicate scrollability */}
            <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-background to-transparent z-10" />
            <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-background to-transparent z-10" />
            
            <div className="overflow-x-auto scrollbar-hide scroll-smooth snap-x snap-mandatory touch-pan-x">
              <TabsList className="inline-flex w-auto min-w-full p-1 gap-2 bg-muted/50">
                <TabsTrigger 
                  value="available" 
                  className={`flex items-center gap-2 whitespace-nowrap data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all duration-300 snap-start ${isMobile ? 'px-4 py-3 text-xs' : 'px-6 py-3 text-sm'}`}
                >
                  Available
                  {jobCounts.available > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      {jobCounts.available}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger 
                  value="picked" 
                  className={`flex items-center gap-2 whitespace-nowrap data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all duration-300 snap-start ${isMobile ? 'px-4 py-3 text-xs' : 'px-6 py-3 text-sm'}`}
                >
                  Picked
                  {jobCounts.picked > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      {jobCounts.picked}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger 
                  value="quoted" 
                  className={`flex items-center gap-2 whitespace-nowrap data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all duration-300 snap-start ${isMobile ? 'px-4 py-3 text-xs' : 'px-6 py-3 text-sm'}`}
                >
                  Quoted
                  {jobCounts.quoted > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      {jobCounts.quoted}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger 
                  value="in-progress" 
                  className={`flex items-center gap-2 whitespace-nowrap data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all duration-300 snap-start ${isMobile ? 'px-4 py-3 text-xs' : 'px-6 py-3 text-sm'}`}
                >
                  In Progress
                  {jobCounts.inProgress > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      {jobCounts.inProgress}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger 
                  value="completed" 
                  className={`flex items-center gap-2 whitespace-nowrap data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all duration-300 snap-start ${isMobile ? 'px-4 py-3 text-xs' : 'px-6 py-3 text-sm'}`}
                >
                  Completed
                  {jobCounts.completed > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      {jobCounts.completed}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>
            </div>
          </div>

          {/* Available Jobs Tab */}
          <TabsContent value="available">

            <div className="mb-8">
              <h2 className="text-3xl font-bold text-foreground mb-2">Find New Opportunities</h2>
              <p className="text-muted-foreground">Jobs matching your expertise within your service radius - respond quickly for best results</p>
            </div>

            {/* Search */}
            <div className="mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Search jobs by title, description, or location..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 shadow-sm hover:shadow-md focus:shadow-md transition-all duration-200"
                />
              </div>
            </div>

            {isLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                <p className="mt-4 text-muted-foreground">Loading available jobs...</p>
              </div>
            ) : filteredJobs.length === 0 ? (
              <Card className="text-center py-16 shadow-elegant hover:shadow-glow transition-all duration-300 border-0 bg-gradient-subtle">
                <CardContent>
                  <div className="relative">
                    <img 
                      src={tradesmanWorkspace} 
                      alt="Professional workspace" 
                      className="w-32 h-32 object-cover rounded-full mx-auto mb-6 shadow-elegant"
                    />
                    <div className="absolute inset-0 bg-gradient-hero rounded-full w-32 h-32 mx-auto opacity-10"></div>
                  </div>
                  <h3 className="text-2xl font-bold text-foreground mb-4">
                    {searchTerm ? "No matching jobs found" : "No jobs available right now"}
                  </h3>
                  <p className="text-muted-foreground text-lg max-w-md mx-auto">
                    {searchTerm 
                      ? "Try adjusting your search terms or check back later"
                      : "New opportunities are posted regularly - check back soon!"
                    }
                  </p>
                  <div className="mt-4 text-sm text-muted-foreground max-w-lg mx-auto">
                    {currentUserProfile?.user_type !== 'tradesman' ? (
                      <p>
                        Tip: You are currently signed in as <span className="font-medium">{currentUserProfile?.user_type || 'unknown'}</span>. Pending job listings are only visible to approved tradesmen. Please sign in with the tradesman account (e.g., Georgios) to view available jobs.
                      </p>
                    ) : !currentUserProfile?.insurance_approved ? (
                      <p>
                        Tip: Your insurance is not approved yet. Pending job listings become visible once your account is approved.
                      </p>
                    ) : (
                      <p>
                        Tip: If you expect to see a specific job, ensure it isn’t archived or dismissed for your account and that it’s within your set distance. You can also widen your service area in Profile.
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'md:grid-cols-2 lg:grid-cols-3'}`}>
                {filteredJobs.map((job) => (
                  <JobCardShell
                    key={job.id}
                    status={job.status}
                    onClick={() => setSelectedJob(job)}
                    highlight={isResponseTimeUrgent(job.response_by)}
                  >
                    <JobCardHeaderSection
                      jobId={job.id}
                      title={job.title}
                      location={getLocationDisplay(job, currentUserProfile?.id || '')}
                      status={job.status}
                      priority={job.priority}
                      jobCategory={undefined}
                      distance={job.distance}
                      extraBadges={
                        isResponseTimeUrgent(job.response_by) ? (
                          <Badge className="bg-destructive/10 text-destructive text-[10px] px-1.5 py-0">
                            <Clock className="h-3 w-3 mr-0.5" /> Urgent
                          </Badge>
                        ) : undefined
                      }
                    />
                    <JobCardDivider />
                    <JobCardBody>
                      <JobCardDescription text={job.description} />
                      <JobCardInfoSection>
                        <JobCardInfoRow
                          label="Budget"
                          value={
                            job.budget_min && job.budget_max
                              ? <span className="text-emerald-600">£{job.budget_min} - £{job.budget_max}</span>
                              : "Not specified"
                          }
                          variant="success"
                        />
                        <JobCardInfoRow
                          label="Required By"
                          value={job.required_date ? safeFormat(job.required_date, "MMM dd, yyyy", "Flexible") : "Flexible"}
                        />
                        <JobCardInfoRow
                          label="Response By"
                          value={
                            <span className={isResponseTimeUrgent(job.response_by) ? 'text-destructive' : ''}>
                              {safeFormat(job.response_by, "MMM dd, HH:mm")}
                            </span>
                          }
                          variant="accent"
                        />
                        {job.required_specialties && job.required_specialties.length > 0 && (
                          <div className="px-3 py-2 bg-primary/5 rounded-lg">
                            <span className="text-xs font-medium text-muted-foreground">Required Skills</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {job.required_specialties.map((s, i) => (
                                <Badge key={i} variant="outline" className="text-[10px]">{s}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </JobCardInfoSection>
                      <JobCardActions>
                        <JobCardActionRow>
                          <JobCardButton
                            onClick={() => handlePickJobForReview(job)}
                            label={pickedJobIds.has(job.id) ? "Pick Again" : "Pick for Review"}
                            variant="default"
                          />
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="outline" size="sm" className="shadow-sm hover:shadow-md transition-all duration-200" onClick={(e) => e.stopPropagation()}>
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDismissJob(job); }}>
                                <X className="h-4 w-4 mr-2" /> Not Interested
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleArchiveJob(job); }}>
                                <Archive className="h-4 w-4 mr-2" /> Archive Job
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </JobCardActionRow>
                        {jobPhotoCounts[job.id] > 0 && (
                          <JobCardButton
                            onClick={() => setPhotoViewingJob(job)}
                            icon={<Camera className="h-4 w-4" />}
                            label={`View Photos (${jobPhotoCounts[job.id]})`}
                            variant="outline"
                          />
                        )}
                      </JobCardActions>
                    </JobCardBody>
                  </JobCardShell>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Picked Jobs Tab */}
          <TabsContent value="picked">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-foreground mb-2">My Picked Jobs</h2>
              <p className="text-muted-foreground">Jobs you have picked for review and discussion with customers</p>
            </div>
            
            {pickedJobs.length === 0 ? (
              <Card className="text-center py-16 shadow-elegant hover:shadow-glow transition-all duration-300 border-0 bg-gradient-subtle">
                <CardContent>
                  <h3 className="text-2xl font-bold text-foreground mb-4">No Picked Jobs Yet</h3>
                  <p className="text-muted-foreground text-lg max-w-md mx-auto">
                    Start responding to available jobs to get selected by customers
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'md:grid-cols-2 lg:grid-cols-3'}`}>
                {pickedJobs.map((job) => (
                   <JobCardShell key={job.id} status={job.status}>
                    <JobCardHeaderSection
                      jobId={job.id}
                      title={job.title}
                      location={getLocationDisplay(job, currentUserProfile?.id || '')}
                      status={job.status}
                      priority={job.priority}
                      distance={job.distance}
                    />
                    <JobCardDivider />
                    <JobCardBody>
                      <JobCardDescription text={job.description} />

                      {/* Customer Rating */}
                      {customerRatings[job.id] && (
                        <div className="flex items-center justify-between p-2.5 bg-primary/5 border border-primary/10 rounded-lg">
                          <div className="flex items-center gap-1.5">
                            <User className="h-3.5 w-3.5 text-primary" />
                            <span className="text-xs font-medium text-primary">Customer Rating</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
                            <span className="text-sm font-semibold">
                              {customerRatings[job.id].average_rating?.toFixed(1) || 'New'}
                            </span>
                            <span className="text-[10px] text-muted-foreground">
                              ({customerRatings[job.id].total_reviews || 0})
                            </span>
                          </div>
                        </div>
                      )}

                      <JobCardInfoSection>
                        <JobCardInfoRow
                          label="Total Cost"
                          value={job.agreed_cost ? <span className="text-emerald-600">£{Number(job.agreed_cost).toFixed(2)}</span> : "Not yet agreed"}
                          variant="success"
                        />
                        <JobCardInfoRow
                          label="Required By"
                          value={job.required_date ? safeFormat(job.required_date, "MMM dd, yyyy", "Flexible") : "Flexible"}
                        />
                        <JobCardInfoRow
                          label="Created"
                          value={safeFormat(job.created_at, "MMM dd, yyyy")}
                          variant="muted"
                        />
                      </JobCardInfoSection>

                      <JobCardActions>
                        <JobCardButton
                          onClick={() => setSelectedJob(job)}
                          icon={<MessageSquare className="h-4 w-4" />}
                          label="Send Quote"
                          variant="default"
                        />
                        <JobCardActionRow>
                          <JobCardButton
                            onClick={() => setJobDetailDialog(job)}
                            icon={<Info className="h-4 w-4" />}
                            label="View Details"
                            fullWidth={false}
                          />
                          <JobCardButton
                            onClick={async () => {
                              if (job.status === 'picked') {
                                try {
                                  const { data: fullJob, error } = await supabase
                                    .from('jobs').select('*').eq('id', job.id).maybeSingle();
                                  if (error) { toast({ title: "Error", description: "Failed to load job details for messaging", variant: "destructive" }); return; }
                                  if (fullJob) setMessagingJob(fullJob);
                                } catch { toast({ title: "Error", description: "Failed to load job details", variant: "destructive" }); }
                              } else { setMessagingJob(job); }
                            }}
                            icon={<MessageCircle className="h-4 w-4" />}
                            label="Chat"
                            fullWidth={false}
                          />
                        </JobCardActionRow>

                        {job.status === 'picked' && (
                          <div className="bg-primary/5 border border-primary/10 rounded-lg p-2.5">
                            <p className="text-xs text-primary font-medium">
                              📞 Chat with the customer to discuss requirements and provide a detailed quote
                            </p>
                          </div>
                        )}

                        {job.status === 'assigned' && (
                          <div className="space-y-2">
                            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2.5">
                              <p className="text-xs text-emerald-800 font-medium mb-2">
                                ✅ You've been assigned this job! Ready to begin?
                              </p>
                              <JobStatusManager job={job} onJobUpdate={fetchJobs} userType="tradesman" />
                            </div>
                            <JobCardButton
                              onClick={() => setTrackingJob(job)}
                              icon={<Navigation className="h-4 w-4" />}
                              label="Navigate to Job"
                            />
                          </div>
                        )}

                        {(job.status === 'in_progress' || job.status === 'completed') && currentUserProfile?.insurance_approved && (
                          <JobCardActionRow>
                            <JobCardButton
                              onClick={() => setPhotoViewingJob(job)}
                              icon={<Camera className="h-4 w-4" />}
                              label={`Photos (${jobPhotoCounts[job.id] || 0})`}
                              fullWidth={false}
                            />
                            <JobStatusManager job={job} onJobUpdate={fetchJobs} userType="tradesman" />
                          </JobCardActionRow>
                        )}

                        {job.status === 'completed' && !completedPaymentJobIds.includes(job.id) && (
                          <JobCardButton
                            onClick={() => handleArchiveJob(job)}
                            icon={<Archive className="h-4 w-4" />}
                            label="Archive Job"
                          />
                        )}

                        {/* Actions for picked pending jobs */}
                        {pickedJobs.some(pj => pj.id === job.id) && job.status === 'pending' && (
                          <div className="space-y-2">
                            <JobCardButton
                              onClick={() => handleUnpickJob(job)}
                              icon={<RotateCcw className="h-4 w-4" />}
                              label="Unpick Job"
                              className="text-primary border-primary/30 hover:border-primary/50"
                            />
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="outline" size="sm" className="w-full shadow-sm hover:shadow-md transition-all duration-200">
                                  <MoreVertical className="h-4 w-4 mr-2" /> More Actions
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleRemovePickedJob(job, true)} className="text-destructive">
                                  <X className="h-4 w-4 mr-2" /> Not Interested & Notify
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleRemovePickedJob(job, false)}>
                                  <X className="h-4 w-4 mr-2" /> Remove Pick Only
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleArchiveJob(job)}>
                                  <Archive className="h-4 w-4 mr-2" /> Archive Job
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        )}

                        {/* Rating Section for completed jobs */}
                        {job.status === 'completed' && currentUserProfile && (
                          <div className="mt-2">
                            <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent mb-3"></div>
                            <JobRatingSection
                              job={job}
                              currentUserProfile={currentUserProfile}
                              onJobArchived={fetchJobs}
                            />
                          </div>
                        )}
                      </JobCardActions>
                    </JobCardBody>
                  </JobCardShell>
                 ))}
               </div>
             )}
          </TabsContent>

          {/* Quoted Jobs Tab */}
          <TabsContent value="quoted">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-foreground mb-2">Jobs I've Quoted</h2>
              <p className="text-muted-foreground">Jobs where you've provided quotes - will move to 'In Progress' after customer payment</p>
            </div>
            
            {quotedJobs.length === 0 ? (
              <Card className="text-center py-16 shadow-elegant hover:shadow-glow transition-all duration-300 border-0 bg-gradient-subtle">
                <CardContent>
                  <h3 className="text-2xl font-bold text-foreground mb-4">No Quoted Jobs Yet</h3>
                  <p className="text-muted-foreground text-lg max-w-md mx-auto">
                    Start responding to available jobs to build your portfolio
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'md:grid-cols-2 lg:grid-cols-3'}`}>
                {quotedJobs.map((response) => (
                   <JobCardShell key={response.id} status={response.jobs.status}>
                    <JobCardHeaderSection
                      jobId={response.jobs.id}
                      title={response.jobs.title}
                      location={getLocationDisplay(response.jobs, currentUserProfile?.id || '')}
                      status={response.jobs.status}
                      distance={response.jobs.distance}
                    />
                    <JobCardDivider />
                    <JobCardBody>
                      <JobCardDescription text={response.jobs.description} />
                      <JobCardInfoSection>
                        <JobCardInfoRow
                          label="My Quote"
                          value={response.estimated_cost ? <span className="text-emerald-600">£{response.estimated_cost}</span> : "Not specified"}
                          variant="success"
                        />
                        <JobCardInfoRow
                          label="Duration"
                          value={response.estimated_duration || "Not specified"}
                        />
                        <JobCardInfoRow
                          label="Response"
                          value={<Badge className={`${getUnifiedStatusColor(response.status)} text-xs`} variant="secondary">{response.status}</Badge>}
                          variant="muted"
                        />
                      </JobCardInfoSection>
                      <JobCardActions>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm" className="w-full shadow-sm hover:shadow-md transition-all duration-200">
                              <MoreVertical className="h-4 w-4 mr-2" /> Job Actions
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleViewQuoteJobDetails(response)}>
                              <Info className="h-4 w-4 mr-2" /> View Job Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setPhotoViewingJob(response.jobs)}>
                              <Camera className="h-4 w-4 mr-2" /> View Photos ({jobPhotoCounts[response.jobs.id] || 0})
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={async () => {
                              try {
                                const { data: fullJob, error } = await supabase.from('jobs').select('*').eq('id', response.jobs.id).maybeSingle();
                                if (error) { toast({ title: 'Error', description: 'Failed to load job details for chat', variant: 'destructive' }); return; }
                                setMessagingJob(fullJob || response.jobs);
                              } catch (err) { setMessagingJob(response.jobs); }
                            }}>
                              <MessageSquare className="h-4 w-4 mr-2" /> Chat with Customer
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleAmendQuote(response)}>
                              <Settings className="h-4 w-4 mr-2" /> Amend Quote
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleCancelQuote(response)}>
                              <X className="h-4 w-4 mr-2" /> Cancel Quote
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleArchiveJob(response.jobs)}>
                              <Archive className="h-4 w-4 mr-2" /> Archive Job
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </JobCardActions>
                    </JobCardBody>
                  </JobCardShell>
                ))}
              </div>
            )}
          </TabsContent>

          {/* In Progress Jobs Tab */}
          <TabsContent value="in-progress">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-foreground mb-2">Jobs In Progress</h2>
              <p className="text-muted-foreground">Active jobs you're currently working on</p>
            </div>

            {myJobs.filter(job => job.status === 'in_progress').length === 0 ? (
              <Card className="text-center py-16 shadow-elegant hover:shadow-glow transition-all duration-300 border-0 bg-gradient-subtle">
                <CardContent>
                  <h3 className="text-2xl font-bold text-foreground mb-4">No Jobs In Progress</h3>
                  <p className="text-muted-foreground text-lg max-w-md mx-auto">
                    When a job starts, it will appear here automatically.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'md:grid-cols-2 lg:grid-cols-3'}`}>
                {myJobs.filter(job => job.status === 'in_progress').map((job) => (
                   <JobCardShell key={job.id} status="in_progress">
                    <JobCardHeaderSection
                      jobId={job.id}
                      title={job.title}
                      location={getLocationDisplay(job, currentUserProfile?.id || '')}
                      status="in_progress"
                      statusLabel="In Progress"
                      priority={job.priority}
                      jobNumber={job.job_number}
                    />
                    <JobCardDivider />
                    <JobCardBody>
                      <JobCardDescription text={job.description} />
                      <JobCardInfoSection>
                        <JobCardInfoRow
                          label="My Quote"
                          value={job.agreed_cost ? <span className="text-emerald-600">£{(job.agreed_cost / 1.03).toFixed(2)}</span> : "Not set"}
                          variant="success"
                        />
                        {job.required_date && (
                          <JobCardInfoRow
                            label="Completion Date"
                            value={safeFormat(job.required_date, "MMM dd, yyyy", "Not specified")}
                          />
                        )}
                      </JobCardInfoSection>
                      <JobCardActions>
                        <JobCardActionRow>
                          <JobCardButton
                            onClick={() => setTrackingJob(job)}
                            icon={<Navigation className="h-4 w-4" />}
                            label="Navigate"
                            fullWidth={false}
                          />
                          <JobCardButton
                            onClick={() => setMessagingJob(job)}
                            icon={<MessageSquare className="h-4 w-4" />}
                            label="Chat"
                            fullWidth={false}
                          />
                        </JobCardActionRow>
                        <JobCardButton
                          onClick={() => setJobDetailDialog(job)}
                          icon={<Info className="h-4 w-4" />}
                          label="See Full Job Information"
                        />
                        <JobStatusManager job={job} onJobUpdate={fetchJobs} userType="tradesman" />
                        <JobCardButton
                          onClick={() => setReportJobDialog(job)}
                          icon={<AlertCircle className="h-4 w-4" />}
                          label="Report Job Issue"
                          variant="destructive"
                        />
                      </JobCardActions>
                    </JobCardBody>
                  </JobCardShell>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="completed">
            {completedDisplayedJobs.length === 0 ? (
              <div className="text-center py-16">
                <h3 className="text-2xl font-bold text-foreground mb-4">Completed Jobs</h3>
                <p className="text-muted-foreground">No completed jobs yet</p>
              </div>
            ) : (
              <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'md:grid-cols-2 lg:grid-cols-3'}`}>
                {completedDisplayedJobs.map((job) => (
                  <JobCardShell key={job.id} status="completed">
                    <JobCardHeaderSection
                      jobId={job.id}
                      title={job.title}
                      location={getLocationDisplay(job, currentUserProfile?.id || '')}
                      status="completed"
                      statusLabel="✓ Completed"
                      jobNumber={job.job_number}
                      topRight={
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={(e) => e.stopPropagation()}>
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setJobDetailDialog(job)}>
                              <Info className="h-4 w-4 mr-2" /> View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setMessagingJob(job)}>
                              <MessageCircle className="h-4 w-4 mr-2" /> View Messages
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleArchiveJob(job)}>
                              <Archive className="h-4 w-4 mr-2" /> Archive Job
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      }
                    />
                    <JobCardDivider />
                    <JobCardBody>
                      <JobCardDescription text={job.description} />
                      <JobCardInfoSection>
                        {job.agreed_cost && (
                          <JobCardInfoRow
                            label="Paid"
                            value={<span className="text-emerald-600">£{Number(job.agreed_cost).toFixed(2)}</span>}
                            variant="success"
                          />
                        )}
                        <JobCardInfoRow
                          label="Completed"
                          value={safeFormat(job.updated_at, "MMM d, yyyy")}
                          variant="muted"
                        />
                      </JobCardInfoSection>

                      {/* Rating Section */}
                      {currentUserProfile && (
                        <div>
                          <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent mb-3"></div>
                          <JobRatingSection
                            job={job}
                            currentUserProfile={currentUserProfile}
                            onJobArchived={fetchJobs}
                          />
                        </div>
                      )}
                    </JobCardBody>
                  </JobCardShell>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Contact Information Footer */}
      <footer className="bg-muted/30 border-t border-border/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <div className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
              <div className="bg-primary/10 p-2 rounded-full">
                <Phone className="h-4 w-4 text-primary" />
              </div>
              <span className="font-medium">0800 043 8880</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
              <div className="bg-accent-orange/10 p-2 rounded-full">
                <Mail className="h-4 w-4 text-accent-orange" />
              </div>
              <span className="font-medium">realise@post.com</span>
            </div>
            <Link to="/how-it-works" className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors font-medium">
              <Award className="h-4 w-4" />
              How It Works
            </Link>
            <LegalDocumentDownloader />
          </div>
        </div>
      </footer>

      {/* Live Chat Widget */}
      <LiveChatWidget />

      {/* Location Controls for active jobs */}
      {myJobs.some(job => job.status === 'assigned' || job.status === 'in_progress') && (
        <div className="fixed bottom-4 left-20 z-50 max-w-sm">
          <LocationControls 
            userType="tradesman" 
            activeJobId={myJobs.find(job => job.status === 'assigned' || job.status === 'in_progress')?.id}
          />
        </div>
      )}

      {/* Contact Verification Dialog */}
      {showVerificationDialog && currentUserProfile && (
        <ContactVerificationDialog
          isOpen={showVerificationDialog}
          onClose={() => setShowVerificationDialog(false)}
          profile={currentUserProfile}
          onVerificationComplete={() => {
            console.log('TradesmanDashboard: onVerificationComplete called');
            // Refresh profile data after verification with longer delay
            setTimeout(async () => {
              try {
                const { data: { user } } = await supabase.auth.getUser();
                if (user) {
                  console.log('TradesmanDashboard: Fetching updated profile for user:', user.id);
                  const { data: profile, error } = await supabase
                    .from("profiles")
                    .select("id, trade_specialties, full_name, user_type, average_rating, total_reviews, email_verified, phone_verified, email, phone, insurance_approved, location, service_radius")
                    .eq("user_id", user.id)
                    .single();
                  
                  if (error) {
                    console.error('TradesmanDashboard: Error fetching profile:', error);
                  } else {
                    console.log('TradesmanDashboard: Updated profile data:', profile);
                    setCurrentUserProfile(profile);
                  }
                }
              } catch (error) {
                console.error('TradesmanDashboard: Error in verification refresh:', error);
              }
            }, 1000); // Increased delay to ensure database update completes
          }}
        />
      )}

      <RespondToJobDialog 
        job={selectedJob} 
        onClose={() => setSelectedJob(null)}
        onJobResponded={fetchJobs}
      />

      <TradesmanTrackingDialog 
        job={trackingJob} 
        onClose={() => setTrackingJob(null)}
      />

      <TradesmanProfileEdit 
        open={showProfileEdit}
        onOpenChange={setShowProfileEdit}
        onProfileUpdated={fetchJobs}
        onSignOut={handleSignOut}
      />

      <JobMessagingDialog 
        job={messagingJob}
        onClose={() => setMessagingJob(null)}
      />

      <JobArchiveDialog
        isOpen={isArchiveDialogOpen}
        onClose={() => {
          setIsArchiveDialogOpen(false);
          setSelectedJobForArchive(null);
        }}
        jobId={selectedJobForArchive?.id || ""}
        jobTitle={selectedJobForArchive?.title || ""}
        onArchived={handleArchiveSuccess}
      />

      <ArchivedJobsDialog
        isOpen={showArchivedJobs}
        onClose={() => setShowArchivedJobs(false)}
        onJobRestored={(restoredJob: any) => {
          // Optimistically unarchive in local state, switch to Completed, and close dialog
          setArchivedJobIds((prev) => {
            const next = new Set(prev);
            next.delete(restoredJob.id);
            return next;
          });

          // Ensure the restored job exists in myJobs immediately
          setMyJobs((prev) => {
            const exists = prev.some((j) => j.id === restoredJob.id);
            const mapped = {
              id: restoredJob.id,
              title: restoredJob.title || '',
              description: restoredJob.description || '',
              location: restoredJob.location || '',
              priority: restoredJob.priority || 'standard',
              status: restoredJob.status || 'completed',
              budget_min: restoredJob.budget_min ?? undefined,
              budget_max: restoredJob.budget_max ?? undefined,
              required_date: restoredJob.required_date || undefined,
              required_specialties: restoredJob.required_specialties || [],
              response_by: restoredJob.response_by || new Date().toISOString(),
              created_at: restoredJob.created_at || new Date().toISOString(),
              updated_at: new Date().toISOString(),
              customer_id: restoredJob.customer_id || '',
              tradesman_id: restoredJob.tradesman_id || null,
              job_number: restoredJob.job_number || 0,
              distance: (prev.find(p => p.id === restoredJob.id)?.distance) || undefined,
            } as Job;

            if (exists) {
              return prev.map((j) => (j.id === restoredJob.id ? { ...j, ...mapped } : j));
            }
            return [mapped, ...prev];
          });

          // Mark as recently restored to avoid flicker during background sync
          restoredRecentlyRef.current.set(restoredJob.id, Date.now());
          // Persistently pin this completed job so it stays visible until you archive it again
          setPinnedCompletedJobIds((prev) => {
            const next = new Set(prev);
            next.add(restoredJob.id);
            return next;
          });
          setPinnedCompletedJobsData(prev => ({
            ...(prev || {}),
            [restoredJob.id]: restoredJob,
          } as Record<string, Job>));
          // Also force-show for a short window to cover any immediate race conditions
          setForceShowJobIds((prev) => {
            const next = new Set(prev);
            next.add(restoredJob.id);
            return next;
          });
          setTimeout(() => {
            setForceShowJobIds((prev) => {
              const next = new Set(prev);
              next.delete(restoredJob.id);
              return next;
            });
          }, 60000);

          setActiveTab('completed');
          setShowArchivedJobs(false);
          // Background refresh
          fetchJobs();
        }}
        refreshTrigger={refreshArchivedTrigger}
      />

      {/* Quote Amendment Dialog */}
      {amendQuoteDialog && (
        <Dialog open={!!amendQuoteDialog} onOpenChange={() => setAmendQuoteDialog(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Amend Your Quote</DialogTitle>
              <DialogDescription>
                Update your quote for "{amendQuoteDialog.jobs?.title}"
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div>
                <label className="text-sm font-medium">New Quote Amount (£)</label>
                <Input
                  type="number"
                  step="0.01"
                  defaultValue={amendQuoteDialog.estimated_cost}
                  id="newQuoteAmount"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Duration</label>
                <Input
                  defaultValue={amendQuoteDialog.estimated_duration}
                  id="newDuration"
                  className="mt-1"
                  placeholder="e.g., 2-3 days"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Reason for Amendment</label>
                <Textarea
                  id="amendmentReason"
                  className="mt-1"
                  placeholder="Explain why you're updating your quote..."
                />
              </div>
              <div className="flex gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setAmendQuoteDialog(null)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={async () => {
                    const newAmount = (document.getElementById('newQuoteAmount') as HTMLInputElement)?.value;
                    const newDuration = (document.getElementById('newDuration') as HTMLInputElement)?.value;
                    const reason = (document.getElementById('amendmentReason') as HTMLTextAreaElement)?.value;
                    
                    if (!newAmount || !reason) {
                      toast({
                        title: "Error",
                        description: "Please provide a new quote amount and reason",
                        variant: "destructive"
                      });
                      return;
                    }

                      try {
                        const { data: rpcResponseId, error: rpcError } = await supabase.rpc('submit_job_response_safe', {
                          p_job_id: amendQuoteDialog.job_id,
                          p_message: `${amendQuoteDialog.message}\n\n[AMENDED] ${reason}`,
                          p_estimated_cost: parseFloat(newAmount),
                          p_estimated_duration: newDuration || null,
                          p_availability: null,
                        });

                        if (rpcError) throw rpcError;

                      toast({
                        title: "Quote Updated",
                        description: "Your quote has been successfully amended"
                      });
                      setAmendQuoteDialog(null);
                      fetchJobs();
                    } catch (error) {
                      console.error('Error amending quote:', error);
                      toast({
                        title: "Error",
                        description: "Failed to update quote",
                        variant: "destructive"
                      });
                    }
                  }}
                  className="flex-1"
                >
                  Update Quote
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Quote Cancellation Dialog */}
      {cancelQuoteDialog && (
        <Dialog open={!!cancelQuoteDialog} onOpenChange={() => setCancelQuoteDialog(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Cancel Your Quote</DialogTitle>
              <DialogDescription>
                Are you sure you want to cancel your quote for "{cancelQuoteDialog.jobs?.title}"?
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div>
                <label className="text-sm font-medium">Reason for Cancellation</label>
                <Textarea
                  id="cancellationReason"
                  className="mt-1"
                  placeholder="Please explain why you're cancelling your quote..."
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setCancelQuoteDialog(null)}
                  className="flex-1"
                >
                  Keep Quote
                </Button>
                <Button
                  variant="destructive"
                  onClick={async () => {
                    const reason = (document.getElementById('cancellationReason') as HTMLTextAreaElement)?.value;
                    
                    if (!reason) {
                      toast({
                        title: "Error",
                        description: "Please provide a reason for cancellation",
                        variant: "destructive"
                      });
                      return;
                    }

                    try {
                      const { error } = await supabase
                        .from('job_responses')
                        .delete()
                        .eq('id', cancelQuoteDialog.id);

                      if (error) throw error;

                      toast({
                        title: "Quote Cancelled",
                        description: "Your quote has been cancelled"
                      });
                      setCancelQuoteDialog(null);
                      fetchJobs();
                    } catch (error) {
                      console.error('Error cancelling quote:', error);
                      toast({
                        title: "Error",
                        description: "Failed to cancel quote",
                        variant: "destructive"
                      });
                    }
                  }}
                  className="flex-1"
                >
                  Cancel Quote
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Quote Job Details Dialog */}
      {viewQuoteJobDetails && (
        <Dialog open={!!viewQuoteJobDetails} onOpenChange={() => setViewQuoteJobDetails(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Job Details</DialogTitle>
              <DialogDescription>
                Complete details for "{viewQuoteJobDetails.jobs?.title}"
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-6 pt-4">
              <div>
                <h3 className="font-semibold text-lg mb-3">Job Information</h3>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm font-medium text-muted-foreground">Job Number:</span>
                      <p className="font-medium">#{viewQuoteJobDetails.jobs?.job_number}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-muted-foreground">Status:</span>
                      <Badge className="ml-2">{viewQuoteJobDetails.jobs?.status}</Badge>
                    </div>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">Location:</span>
                    <p className="font-medium">{viewQuoteJobDetails.jobs ? getLocationDisplay(viewQuoteJobDetails.jobs, currentUserProfile?.id || '') : 'Location not specified'}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">Description:</span>
                    <p className="mt-1">{viewQuoteJobDetails.jobs?.description}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm font-medium text-muted-foreground">Budget Range:</span>
                      <p className="font-medium">
                        £{viewQuoteJobDetails.jobs?.budget_min} - £{viewQuoteJobDetails.jobs?.budget_max}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-muted-foreground">Priority:</span>
                      <p className="font-medium">{viewQuoteJobDetails.jobs?.priority}</p>
                    </div>
                  </div>
                  {viewQuoteJobDetails.jobs?.required_date && (
                    <div>
                      <span className="text-sm font-medium text-muted-foreground">Required Date:</span>
                      <p className="font-medium">{format(new Date(viewQuoteJobDetails.jobs.required_date), "PPP")}</p>
                    </div>
                  )}
                  {viewQuoteJobDetails.jobs?.required_specialties && (
                    <div>
                      <span className="text-sm font-medium text-muted-foreground">Required Specialties:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {viewQuoteJobDetails.jobs.required_specialties.map((specialty: string) => (
                          <Badge key={specialty} variant="outline">{specialty}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <h3 className="font-semibold text-lg mb-3">Your Quote</h3>
                <div className="bg-green-50 p-4 rounded-lg space-y-2">
                  <div className="flex justify-between">
                    <span className="font-medium">Quote Amount:</span>
                    <span className="font-bold text-green-600">£{viewQuoteJobDetails.estimated_cost}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Duration:</span>
                    <span>{viewQuoteJobDetails.estimated_duration || "Not specified"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Status:</span>
                    <Badge>{viewQuoteJobDetails.status}</Badge>
                  </div>
                  {viewQuoteJobDetails.availability && (
                    <div className="flex justify-between">
                      <span className="font-medium">Available From:</span>
                      <span>{format(new Date(viewQuoteJobDetails.availability), "PPP")}</span>
                    </div>
                  )}
                  {viewQuoteJobDetails.message && (
                    <div>
                      <span className="font-medium">Your Message:</span>
                      <p className="mt-1 text-sm">{viewQuoteJobDetails.message}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <JobPhotoManager
        jobId={photoViewingJob?.id || ""}
        open={!!photoViewingJob}
        onOpenChange={(open) => !open && setPhotoViewingJob(null)}
        isCustomer={false}
      />

      {/* User Ratings Dialog */}
      {showUserRatingsDialog && currentUserProfile && (
        <UserRatingsDialog
          isOpen={showUserRatingsDialog}
          onClose={() => setShowUserRatingsDialog(false)}
          currentUserProfile={currentUserProfile}
        />
      )}

      {/* Tradesman Reviews Dialog - Shows reviews RECEIVED */}
      <TradesmanRatingsDialog
        tradesmanId={currentUserProfile?.id || null}
        open={showTradesmanReviewsDialog}
        onOpenChange={setShowTradesmanReviewsDialog}
      />

      {/* Tradesman Job Detail Dialog */}
      <TradesmanJobDetailDialog
        job={jobDetailDialog}
        open={!!jobDetailDialog}
        onOpenChange={(open) => !open && setJobDetailDialog(null)}
        onNavigateToJob={() => {
          if (jobDetailDialog) {
            setTrackingJob(jobDetailDialog);
            setJobDetailDialog(null);
          }
        }}
        onMessageCustomer={() => {
          if (jobDetailDialog) {
            setMessagingJob(jobDetailDialog);
            setJobDetailDialog(null);
          }
        }}
      />
      
      {/* Global Chat Inbox */}
      <ChatInboxDialog 
        open={showChatInbox} 
        onOpenChange={(o) => { setShowChatInbox(o); setChatOpen(o); }}
        onUnreadCountChange={setUnreadCount}
      />
      
      {/* Floating App Download Widget */}
      
      <ReportJobIssueDialog
        job={reportJobDialog}
        onClose={() => setReportJobDialog(null)}
      />

      {/* Password Reset Dialog */}
      <PasswordResetDialog
        open={showPasswordReset}
        onOpenChange={onPasswordResetChange}
      />

      {/* Profile Incomplete Warning Dialog */}
      <TradesmanProfileIncompleteDialog tradesmanProfile={currentUserProfile} />

      <FloatingAppDownload />
    </div>
  );
};

export default TradesmanDashboard;
